import React, { useEffect, useState } from "react";
import { LocalUser, RemoteUser } from "agora-rtc-react";

import { BsFillMicMuteFill, BsFillMicFill, BsSpeaker, BsMic } from "react-icons/bs";
import { IoVideocam, IoCameraReverse, IoVideocamOff, IoArrowBack } from "react-icons/io5";
import { ImPhoneHangUp } from "react-icons/im";
import { VscLoading } from "react-icons/vsc";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import { useScheduleCall } from "@/hooks/use-schedule-call";
import { useUnauthorizedRedirect } from "@/hooks/use-unauthorized-redirect";
import { useAuth } from "@/hooks/use-auth";
import SessionTimer from "@/components/SessionTimer";
import Generatedkundli from "@/components/Meeting/Generatedkundli";
import InCallChat from "@/components/InCallChat";

const UserAvatar = ({ name, imageUrl, className }) => (
  <div
    className={`${className} bg-yellow-100 rounded-full flex items-center justify-center overflow-hidden`}
  >
    <img
      src={imageUrl || "/path/to/default-avatar.png"}
      alt={name}
      className="h-full w-full object-cover"
      onError={(e) => {
        e.target.style.display = "none";
        e.target.parentElement.innerHTML = `
          <div class="h-full w-full flex items-center justify-center text-2xl font-semibold text-gray-500">
            ${name?.charAt(0) || "U"}
          </div>
        `;
      }}
    />
  </div>
);

export default function ScheduleVideoCallPage() {
  useUnauthorizedRedirect();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(true);
  const navigate = useNavigate();
  const { astrologer } = useAuth();

  const {
    localCameraTrack,
    localMicrophoneTrack,
    cameraOn,
    micOn,
    remoteUsers,
    setCamera,
    setMic,
    handleCameraSwitch,
    isConnected,
    setIsUserReady,
    isUserReady,
    isPageReady,
    isLoading,
    microphoneDevices,
    handleMicrophoneDeviceChange,
    speakerDevices,
    handleSpeakerDeviceChange,
    cameraDevices,
    handleCameraDeviceChange,

    // D1 partner-app billing wiring. `meeting` is fetched by id (not
    // guessed from appointments[0] like before). `endCall` handles
    // billing-end + meeting state flip in the right order. `billing`
    // is the partner-scoped snapshot used by the Earned tile.
    meeting,
    billing,
    endCall,
  } = useScheduleCall("video");

  // Derive user + appointment display from the hook-provided meeting.
  // Falls back to defaults if the by-id fetch hasn't resolved yet —
  // the screen still renders, just with neutral placeholders until
  // the populated meeting arrives.
  const user = {
    name: meeting?.user?.name || "User",
    imageUrl: meeting?.user?.picture || null,
  };

  // The screen used to flip isUserReady=true after a 1s setTimeout so
  // Agora media tracks had time to initialize. Preserve that behaviour;
  // the hook now also owns the actual leave logic so this just guards
  // the initial publish window.
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsUserReady(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, [setIsUserReady]);

  const handleEndCall = async () => {
    // Order matters: close the modal first so the navigate-away below
    // isn't blocked by an open Portal animation.
    setShowConfirmModal(false);
    // endCall is idempotent and:
    //   1. ends the billing session (server flips Meeting → completed
    //      via meetingState service AND records billed amount)
    //   2. defensively updates meeting state for zero-duration cases
    //   3. detaches Agora via setIsUserReady(false)
    // Then we navigate. The 'astrologer_ended' reason maps to
    // attribution='astrologer' but does NOT trigger the user's
    // partner-disconnect auto-refund (voluntary hangup).
    await endCall("astrologer_ended");
    navigate("/connect/meeting-end");
  };

  const toggleSpeaker = async () => {
    setSpeakerOn(!speakerOn);
    if (speakerDevices && speakerDevices.length > 1) {
      const currentIdx = speakerDevices.findIndex(s => s.isActive);
      const nextIdx = (currentIdx + 1) % speakerDevices.length;
      const nextDevice = speakerDevices[nextIdx];
      if (nextDevice && nextDevice.deviceId) {
        await handleSpeakerDeviceChange({ target: { value: nextDevice.deviceId } });
      }
    }
  };

  if (!isPageReady || isLoading)
    return (
      <div className="flex bg-black justify-center items-center w-full h-screen flex-col relative">
        <VscLoading size={50} color="#f16631" className="animate-spin" />
      </div>
    );

  // Format appointment date and time from the hook-provided meeting.
  // Falls back to "now" while the by-id fetch is in flight so the
  // bottom bar always renders something legible. The display string
  // moves from "now" to the actual booking when meeting resolves.
  const formattedDateTime = meeting?.date && meeting?.time
    ? (() => {
        const [hour, minute] = meeting.time.split(":").map(Number);
        const dateTime = moment(meeting.date).hour(hour).minute(minute);
        return {
          date: dateTime.format("MMMM D, YYYY"),
          time: dateTime.format("h:mm A"),
        };
      })()
    : {
        date: new Date().toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
        time: new Date().toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        }),
      };

  return (
    <div className="flex flex-col w-full h-screen bg-gray-50 p-7">
      <div className="flex w-full overflow-hidden gap-4">
        <div className="w-full relative bg-[#E1E1E1] rounded-xl mb-4">
          <div className="absolute top-0 right-4 z-30">
            {/*
              SessionTimer's onDurationChange used to drive a local
              `setCallDuration` state. With the hook now owning duration
              (wall-clock, used on leave), we pass a no-op — the timer
              still displays elapsed time off its own internal clock,
              which is fine for visual feedback. The authoritative
              value sent to the server on end is the hook's `duration`.
            */}
            <SessionTimer
              isConnected={isConnected}
              isUserReady={isUserReady}
              onDurationChange={() => {}}
            />
            {/* Earnings tile — surfaces partner-scoped billing snapshot
                during the call. Server polls every 15s; we render the
                most recent snapshot. Only shown once billing is active
                (i.e., once startBilling has succeeded from the user side). */}
            {billing?.isActive && billing?.ratePerMinute > 0 && (
              <div className="mt-2 rounded-lg bg-white/90 px-3 py-1.5 text-right shadow-sm border border-orange-200">
                <div className="text-[10px] font-bold uppercase tracking-widest text-orange-600">
                  Earned
                </div>
                <div className="text-base font-semibold text-gray-800">
                  ₹{Math.round(billing.totalBilledAmount || 0)}
                </div>
                <div className="text-[10px] text-gray-500">
                  ₹{billing.ratePerMinute}/min
                </div>
              </div>
            )}
          </div>

          {/* Main video container */}
          <div className="w-full h-full flex-1 flex justify-center items-center relative">
            <RemoteUser
              playAudio={false}
              user={remoteUsers[0]}
              cover={() => (
                <div className="w-full h-full justify-center items-center flex bg-[#eee]">
                  <UserAvatar
                    name={user.name}
                    imageUrl={user.imageUrl}
                    className="w-32 h-32"
                  />
                  <div className="absolute bottom-3 left-4 text-lg font-medium text-gray-800">
                    {user.name}
                  </div>
                </div>
              )}
            />

            {/* Local user (astrologer) video preview */}
            <div className="absolute h-72 sm:h-auto sm:min-w-72 aspect-[9/16] sm:w-1/4 sm:aspect-video bottom-3 right-3 z-10">
              <LocalUser
                playAudio={false}
                audioTrack={localMicrophoneTrack}
                cameraOn={cameraOn}
                micOn={micOn}
                videoTrack={localCameraTrack}
                className="rounded-xl"
                cover={() => (
                  <div className="w-full h-full justify-center items-center flex bg-[#eee] border border-black">
                    <UserAvatar
                      name={astrologer?.personalInformation?.name}
                      imageUrl={astrologer?.personalInformation?.picture}
                      className="w-24 h-24"
                    />
                    <div className="absolute bottom-1 left-1 text-sm font-medium text-gray-800 bg-white bg-opacity-70 px-2 py-1 rounded">
                      {astrologer?.personalInformation?.name || "Astrologer"}
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        </div>

        <div className="w-5/12 bg-white border-l border-gray-200 flex flex-col overflow-auto rounded-xl">
          <Generatedkundli />
          {/* Chat section */}
          <div className="p-4 border-t">
            <h3 className="font-semibold text-gray-800 mb-2">Chat</h3>
            {meeting?.user?._id ? (
              <InCallChat
                recipientId={`user_${meeting.user._id}`}
                recipientName={user.name}
              />
            ) : (
              <p className="text-gray-500 text-sm">Chat unavailable</p>
            )}
          </div>
        </div>
      </div>

      <div className="py-4 px-6">
        <div className="flex items-center justify-center relative">
          <div className="flex flex-col absolute left-4">
            <div className="text-lg font-semibold text-gray-800">
              Video Call Consultation
            </div>
            <div className="text-sm text-gray-500">
              {formattedDateTime.date} | {formattedDateTime.time}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex flex-col items-center">
              <button
                onClick={handleCameraSwitch}
                className="w-16 h-16 rounded-full flex flex-col items-center justify-center bg-orange-100 text-orange-500 shadow-md"
              >
                <IoCameraReverse size={24} />
              </button>
              <span className="text-xs mt-1">Switch</span>
            </div>
            <div className="flex flex-col items-center">
              <button
                onClick={toggleSpeaker}
                className={`w-16 h-16 rounded-full flex flex-col items-center justify-center ${speakerOn ? "bg-orange-100 text-orange-500" : "bg-gray-200 text-gray-500"} shadow-md`}
              >
                {speakerOn ? <BsSpeaker size={24} /> : <BsSpeaker style={{ opacity: 0.3 }} size={24} />}
              </button>
              <span className="text-xs mt-1">Speaker</span>
            </div>
            <div className="flex flex-col items-center">
              <button
                onClick={() => setMic((prev) => !prev)}
                className={`w-16 h-16 rounded-full flex flex-col items-center justify-center ${micOn ? "bg-orange-100 text-orange-500" : "bg-gray-200 text-gray-500"} shadow-md`}
              >
                {micOn ? <BsMic size={24} /> : <BsMic style={{ opacity: 0.3 }} size={24} />}
              </button>
              <span className="text-xs mt-1">Mic</span>
            </div>
            <div className="flex flex-col items-center">
              <button
                onClick={() => setCamera((prev) => !prev)}
                className={`w-16 h-16 rounded-full flex flex-col items-center justify-center ${cameraOn ? "bg-orange-100 text-orange-500" : "bg-gray-200 text-gray-500"} shadow-md`}
              >
                {cameraOn ? <IoVideocam size={24} /> : <IoVideocamOff style={{ opacity: 0.3 }} size={24} />}
              </button>
              <span className="text-xs mt-1">Camera</span>
            </div>
            <div className="flex flex-col items-center">
              <button
                className="w-16 h-16 rounded-full bg-red-500 text-white flex flex-col items-center justify-center shadow-md"
                onClick={() => setShowConfirmModal(true)}
              >
                <ImPhoneHangUp size={24} />
              </button>
              <span className="text-xs mt-1">Leave Call</span>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onConfirm={handleEndCall}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
}

export const ConfirmationModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            End Session
          </h3>
          <p className="text-gray-600 mb-6">
            Do you want to complete the session?
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              End Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// {
//   <div className="justify-center items-center gap-5 p-2 flex-wrap hidden sm:flex">
//     {microphoneDevices && (
//       <div>
//         <h3 className="text-center font-semibold">Microphone Devices</h3>
//         <select className="truncate" onChange={handleMicrophoneDeviceChange}>
//           {microphoneDevices.map((m, i) => (
//             <option className="truncate" value={m.deviceId} key={i}>
//               {m.label}
//             </option>
//           ))}
//         </select>
//       </div>
//     )}
//     {cameraDevices && (
//       <div>
//         <h3 className="text-center font-semibold">Camera Devices</h3>
//         <select className="truncate" onChange={handleCameraDeviceChange}>
//           {cameraDevices.map((c, i) => (
//             <option className="truncate" value={c.deviceId} key={i}>
//               {c.label}
//             </option>
//           ))}
//         </select>
//       </div>
//     )}
//     {speakerDevices && (
//       <div>
//         <h3 className="text-center font-semibold ">Speaker Devices</h3>
//         <select className="truncate" onChange={handleSpeakerDeviceChange}>
//           {speakerDevices.map((s, i) => (
//             <option className="truncate" value={s.deviceId} key={i}>
//               {s.label}
//             </option>
//           ))}
//         </select>
//       </div>
//     )}
//   </div>;
// }
